import { z } from "zod";
import {
  HiringProcessAssigneeRole,
  HiringProcessItemType,
} from "@api/shared-runtime/enums";

/** Schema for an inline form definition proposed by the AI (not yet persisted). */
const newFormSpecSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().max(2000).nullable().optional(),
  fields: z
    .array(
      z.object({
        label: z.string().min(1).max(500),
        description: z.string().max(2000).nullable().optional(),
        fieldType: z.string(),
        sortOrder: z.number().int().min(0),
        isRequired: z.boolean(),
        options: z.array(z.string()).nullable().optional(),
      }),
    )
    .min(1),
});

const hiringProcessItemLinkSchema = z.object({
  /** DB id when editing an existing link. */
  id: z.string().uuid().optional(),
  url: z.string().url("Must be a valid URL").max(2000),
  label: z.string().max(200).nullable().optional(),
  sortOrder: z.number().int().min(0),
});

const hiringProcessItemSchema = z.object({
  /** DB id when editing an existing item. Enables rename + removal detection. */
  id: z.string().uuid().optional(),
  /**
   * Opaque ref used to wire predecessors in the same save. Existing items
   * reuse their DB id; new items get a generated ref on the client.
   */
  ref: z.string().optional(),
  /**
   * Predecessors this item waits on. Each can optionally share its content
   * (files/links) with this item when flagged.
   */
  predecessors: z
    .array(
      z.object({
        ref: z.string(),
        shareContent: z.boolean(),
      }),
    )
    .optional()
    .default([]),
  title: z.string().min(1, "Title is required").max(500),
  description: z.string().max(2000).nullable().optional(),
  sortOrder: z.number().int().min(0),
  isRequired: z.boolean(),
  assigneeRole: z.nativeEnum(HiringProcessAssigneeRole),
  itemType: z
    .nativeEnum(HiringProcessItemType)
    .default(HiringProcessItemType.Task),
  formId: z.string().uuid().nullable().optional(),
  /** Inline form spec proposed by AI — created on save, then replaced with formId */
  newFormSpec: newFormSpecSchema.nullable().optional(),
  richTextContent: z.string().nullable().optional(),
  /** IDs of already-uploaded files (present when editing an existing template) */
  fileUploadIds: z.array(z.string().uuid()).optional().default([]),
  /** New files to upload (metadata only — TUS upload happens after save) */
  newFiles: z
    .array(
      z.object({
        fileName: z.string(),
        fileSizeBytes: z.number(),
        mimeType: z.string(),
        sha256: z.string().optional(),
      }),
    )
    .optional(),
  /** Template-level external links (shared with the item's content). */
  links: z.array(hiringProcessItemLinkSchema).optional().default([]),
  /**
   * For provider-assigned `document_upload` items: optional hint to which
   * credential file type (smallint DB id) the org is asking for. When set,
   * the provider's upload UI offers attaching from the credentials library
   * and defaults to saving fresh uploads back to the library. null = no hint.
   */
  requestedCredentialFileTypeId: z
    .number()
    .int()
    .positive()
    .nullable()
    .optional(),
});

const hiringProcessStageSchema = z.object({
  /** DB id when editing an existing stage. Enables rename + removal detection. */
  id: z.string().uuid().optional(),
  name: z.string().min(1, "Stage name is required").max(200),
  description: z.string().max(2000).nullable().optional(),
  sortOrder: z.number().int().min(0),
  items: z.array(hiringProcessItemSchema),
});

export const hiringProcessTemplateFormSchema = z.object({
  name: z.string().min(1, "Name is required").max(200),
  description: z.string().max(2000).nullable().optional(),
  isDefault: z.boolean(),
  stages: z
    .array(hiringProcessStageSchema)
    .min(1, "At least one stage is required"),
});

export type HiringProcessTemplateFormValues = z.infer<
  typeof hiringProcessTemplateFormSchema
>;

export interface ExistingFileAttachment {
  fileUploadId: string;
  fileName: string;
  mimeType: string;
  fileSizeBytes: number;
  scanStatus?: string;
}

/**
 * Keyed by `${stageIndex}-${itemIndex}`.
 * Each entry is the list of already-uploaded files for that item.
 */
export type ExistingAttachmentsMap = Map<string, ExistingFileAttachment[]>;

export interface HiringProcessTemplateSubmitPayload {
  values: HiringProcessTemplateFormValues;
  localFiles: Map<string, File[]>;
  /** Stage ids the user explicitly deleted or confirmed as "different object". */
  removedStageIds: string[];
  /** Item ids the user explicitly deleted or confirmed as "different object". */
  removedItemIds: string[];
}

export type RenamePrompt =
  | {
      kind: "stage";
      id: string;
      oldName: string;
      newName: string;
    }
  | {
      kind: "item";
      id: string;
      stageName: string;
      oldName: string;
      newName: string;
    };

export type RenameChoice = "rename" | "recreate";

/**
 * Non-optional warning: the user changed itemType or assigneeRole on an
 * existing item, which forces a delete+create on the backend. Shown in the
 * same dialog as rename prompts, informational only (no choice to make).
 */
export type ForceReplaceNotice = {
  id: string;
  stageName: string;
  itemName: string;
  reason: string;
};
